/**
 * Desktop mega-nav: open/close + equalize second-level link heights.
 *
 * The Solutions panel is viewport-centered, so the path into column 2
 * crosses sibling labels. A delayed switch lets that transit finish
 * without opening the sibling; pausing on a sibling still switches.
 */

const ARCADIA_MEGA_NAV_DESKTOP_MIN = 1024;
const ARCADIA_MEGA_NAV_OPEN_CLASS = 'is-desktop-open';
const ARCADIA_MEGA_NAV_CLOSE_DELAY = 280;
const ARCADIA_MEGA_NAV_SWITCH_DELAY = 180;
const ARCADIA_MEGA_NAV_GAP = 14;

function arcadiaIsDesktopMegaNav() {
	return window.matchMedia(`(min-width: ${ARCADIA_MEGA_NAV_DESKTOP_MIN}px)`).matches;
}

function arcadiaIsHeaderSearchOpen() {
	return Boolean(document.querySelector('.site-header__search-dropdown.is-open'));
}

function arcadiaResetMegaPanelLinkHeights(panel) {
	panel.querySelectorAll(':scope > .menu-item > .menu-link').forEach((link) => {
		link.style.minHeight = '';
	});
}

function arcadiaEqualizeMegaPanelLinkHeights(panel) {
	const links = Array.from(panel.querySelectorAll(':scope > .menu-item > .menu-link'));
	if (links.length === 0) {
		return;
	}

	links.forEach((link) => {
		link.style.minHeight = '';
	});

	const maxHeight = links.reduce((max, link) => Math.max(max, link.getBoundingClientRect().height), 0);
	if (maxHeight <= 0) {
		return;
	}

	const minHeight = `${Math.ceil(maxHeight)}px`;
	links.forEach((link) => {
		link.style.minHeight = minHeight;
	});
}

function arcadiaScheduleMegaPanelLinkHeights(panel) {
	window.requestAnimationFrame(() => {
		window.requestAnimationFrame(() => {
			if (arcadiaIsDesktopMegaNav()) {
				arcadiaEqualizeMegaPanelLinkHeights(panel);
			}
		});
	});
}

function arcadiaPointerOverDesktopPanel(item, clientX, clientY) {
	const wrap = item.querySelector(':scope > .mega-panel-wrap');
	if (!wrap) {
		return false;
	}

	const rect = wrap.getBoundingClientRect();
	return (
		clientX >= rect.left &&
		clientX <= rect.right &&
		clientY >= rect.top - ARCADIA_MEGA_NAV_GAP &&
		clientY <= rect.bottom
	);
}

export function initMegaNav() {
	const header = document.getElementById('arcadia-header');
	if (!header) {
		return;
	}

	const allTopItems = Array.from(header.querySelectorAll('.level-0 > .menu-item'));
	const topItems = allTopItems.filter((item) => item.classList.contains('has-children'));
	if (topItems.length === 0) {
		return;
	}

	let closeTimer = 0;
	let openTimer = 0;

	const clearTimers = () => {
		window.clearTimeout(closeTimer);
		window.clearTimeout(openTimer);
	};

	const closeItem = (item) => {
		item.classList.remove(ARCADIA_MEGA_NAV_OPEN_CLASS);
	};

	const closeAll = () => {
		topItems.forEach(closeItem);
	};

	const openItem = (item) => {
		if (arcadiaIsHeaderSearchOpen()) {
			closeAll();
			return;
		}

		clearTimers();
		topItems.forEach((other) => {
			if (other !== item) {
				closeItem(other);
			}
		});
		item.classList.add(ARCADIA_MEGA_NAV_OPEN_CLASS);
		const panel = item.querySelector('.mega-panel');
		if (panel) {
			arcadiaScheduleMegaPanelLinkHeights(panel);
		}
	};

	const scheduleClose = (item) => {
		window.clearTimeout(closeTimer);
		closeTimer = window.setTimeout(() => {
			if (!item.matches(':focus-within')) {
				closeItem(item);
			}
		}, ARCADIA_MEGA_NAV_CLOSE_DELAY);
	};

	const scheduleOpen = (item) => {
		window.clearTimeout(openTimer);
		openTimer = window.setTimeout(() => {
			if (item.classList.contains('has-children')) {
				openItem(item);
			} else {
				closeAll();
			}
		}, ARCADIA_MEGA_NAV_SWITCH_DELAY);
	};

	allTopItems.forEach((topItem) => {
		const panel = topItem.querySelector('.mega-panel');

		topItem.addEventListener('mouseenter', () => {
			if (!arcadiaIsDesktopMegaNav() || arcadiaIsHeaderSearchOpen()) {
				return;
			}

			const openSibling = topItems.find(
				(item) => item !== topItem && item.classList.contains(ARCADIA_MEGA_NAV_OPEN_CLASS)
			);
			if (openSibling) {
				scheduleOpen(topItem);
				return;
			}

			if (panel) {
				openItem(topItem);
			}
		});

		topItem.addEventListener('mouseleave', (event) => {
			if (!arcadiaIsDesktopMegaNav()) {
				return;
			}
			window.clearTimeout(openTimer);
			const next = event.relatedTarget;
			if (next instanceof Node && topItem.contains(next)) {
				return;
			}
			if (topItem.classList.contains(ARCADIA_MEGA_NAV_OPEN_CLASS)) {
				scheduleClose(topItem);
			}
		});

		topItem.addEventListener('focusin', () => {
			if (!panel || arcadiaIsHeaderSearchOpen()) {
				return;
			}
			if (arcadiaIsDesktopMegaNav()) {
				openItem(topItem);
			} else {
				arcadiaScheduleMegaPanelLinkHeights(panel);
			}
		});

		topItem.addEventListener('focusout', (event) => {
			if (!arcadiaIsDesktopMegaNav() || !panel) {
				return;
			}
			const next = event.relatedTarget;
			if (next instanceof Node && topItem.contains(next)) {
				return;
			}
			scheduleClose(topItem);
		});

		if (!panel) {
			return;
		}

		const wrap = topItem.querySelector(':scope > .mega-panel-wrap');
		if (!wrap) {
			return;
		}

		const resetWhenClosed = () => {
			if (
				topItem.classList.contains(ARCADIA_MEGA_NAV_OPEN_CLASS) ||
				topItem.matches(':hover') ||
				topItem.contains(document.activeElement)
			) {
				return;
			}
			arcadiaResetMegaPanelLinkHeights(panel);
		};

		wrap.addEventListener('transitionend', (event) => {
			if (event.target !== wrap) {
				return;
			}
			if (event.propertyName !== 'opacity' && event.propertyName !== 'visibility') {
				return;
			}
			resetWhenClosed();
		});

		topItem.addEventListener('mouseleave', () => {
			window.setTimeout(resetWhenClosed, 500);
		});
	});

	document.addEventListener('mousemove', (event) => {
		if (!arcadiaIsDesktopMegaNav()) {
			return;
		}

		const openItemEl = topItems.find((item) => item.classList.contains(ARCADIA_MEGA_NAV_OPEN_CLASS));
		if (openItemEl && arcadiaPointerOverDesktopPanel(openItemEl, event.clientX, event.clientY)) {
			clearTimers();
		}
	});

	window.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			closeAll();
		}
	});

	window.addEventListener('resize', () => {
		topItems.forEach((topItem) => {
			const itemPanel = topItem.querySelector('.mega-panel');
			if (!itemPanel) {
				return;
			}

			if (!arcadiaIsDesktopMegaNav()) {
				arcadiaResetMegaPanelLinkHeights(itemPanel);
				return;
			}

			if (
				topItem.classList.contains(ARCADIA_MEGA_NAV_OPEN_CLASS) ||
				topItem.contains(document.activeElement)
			) {
				arcadiaScheduleMegaPanelLinkHeights(itemPanel);
			}
		});
	});
}
